// Live state for the Cue WebSocket connection - see client.ts's own
// header comment for what Cue is. A plain module-level store, not part of
// CueClient itself: StoreContext.tsx's currentCueClient() rebuilds the
// actual CueClient whenever the account's security credentials change
// (see that function's own comment), so a screen wanting to show "is Cue
// connected" has no single stable instance to hold onto - this sits
// outside that lifecycle instead, and every CueClient that ever exists in
// this tab reports into the same store. Mirrors
// ios/zmist/Models/CueClient.swift's CueDiagnosticsMonitor, same reason.
//
// Framework-agnostic on purpose (no React import here) - see
// useCueDiagnostics.ts for the hook that subscribes a component to this.
import { CueCommand, MESSAGE_VALUE_SEP } from './wire';

export type CueConnectionState = 'disconnected' | 'connecting' | 'connected';

// Per-channel Cue room membership for this tab. 'joining' is set the moment
// a "c" join is sent (or when a reconnect is pending after a drop);
// 'joined' once the server acks it 202. A channel with no entry at all has
// never been joined this session. Keyed by channelId (the nickname, on
// web). Drives the connection dot ChannelsListScreen shows per row and the
// tint on ChatScreen's info button.
export type CueRoomState = 'joining' | 'joined';

export type CueEventKind = 'command' | 'reply' | 'relay' | 'timeout' | 'error';

// Structured fields instead of one flattened "detail" string - kept this
// way specifically so the UI (describeCueEvent below) can say "Sent
// template signal #2 to @newchannel" rather than parsing "mt @newchannel.1
// 2" back apart. Only the fields that apply to a given kind are set.
export interface CueDiagnosticEvent {
  id: string;
  timestamp: number;
  direction: 'out' | 'in';
  kind: CueEventKind;
  command?: string; // wire command code ('c', 'm', 'mt', 'su') - see wire.ts's CueCommand
  channelId?: string;
  value?: string; // outbound message text, or an "mt" command's template number
  status?: number; // reply status code
  rttMs?: number; // reply/heartbeat round-trip time
  note?: string; // free-text explanation, only for 'error' events
}

// Per-channel relay tallies for this tab's lifetime - "in" is relays this
// client received for the channel, "out" is messages/signals/rounds it
// sent into it. Keyed by channelId (the nickname, on web).
export interface CueChannelTraffic {
  in: number;
  out: number;
}

// Running totals since the tab opened (or since resetCueCounters). The
// event ring buffer above is capped at MAX_EVENTS; these keep counting
// past that so the Diagnostics screen can show lifetime traffic, not just
// "the last 100 things".
export interface CueCounters {
  messagesSent: number; // outbound m / mt
  messagesReceived: number; // inbound relays (any command)
  roundsSent: number; // outbound r
  joinsSent: number; // outbound c (includes reconnect rejoins)
  heartbeatsSent: number;
  heartbeatsAcked: number;
  timeouts: number;
  errors: number;
  byChannel: Record<string, CueChannelTraffic>;
}

export interface CueDiagnosticsSnapshot {
  connectionState: CueConnectionState;
  lastHeartbeatAt: number | null;
  lastHeartbeatRttMs: number | null;
  events: CueDiagnosticEvent[];
  counters: CueCounters;
  // Per-channel room membership (see CueRoomState) and the timestamp of the
  // last inbound relay for each channel - the latter drives the brief
  // "blink" on the channel-list dot / info button when a signal lands.
  rooms: Record<string, CueRoomState>;
  lastActivityAt: Record<string, number>;
}

// Ring buffer cap - enough recent history to be useful in the Diagnostics
// screen without growing unbounded for a long-running tab.
const MAX_EVENTS = 100;

function emptyCounters(): CueCounters {
  return {
    messagesSent: 0,
    messagesReceived: 0,
    roundsSent: 0,
    joinsSent: 0,
    heartbeatsSent: 0,
    heartbeatsAcked: 0,
    timeouts: 0,
    errors: 0,
    byChannel: {},
  };
}

let state: CueDiagnosticsSnapshot = {
  connectionState: 'disconnected',
  lastHeartbeatAt: null,
  lastHeartbeatRttMs: null,
  events: [],
  counters: emptyCounters(),
  rooms: {},
  lastActivityAt: {},
};
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

export function setCueConnectionState(connectionState: CueConnectionState): void {
  // A dropped socket keeps its room keys (the client rejoins them all - see
  // client.ts's scheduleReconnect/rejoinAll) but they're no longer live, so
  // downgrade every 'joined' back to 'joining' until the rejoin acks land.
  const rooms =
    connectionState === 'connected'
      ? state.rooms
      : Object.fromEntries(Object.keys(state.rooms).map((id) => [id, 'joining' as CueRoomState]));
  state = { ...state, connectionState, rooms };
  notify();
}

/** Sets one channel's room membership - see CueRoomState. Called by
 * CueClient as it sends/acks a "c" join (and on reconnect rejoins). */
export function setCueRoomState(channelId: string, roomState: CueRoomState): void {
  if (state.rooms[channelId] === roomState) return;
  state = { ...state, rooms: { ...state.rooms, [channelId]: roomState } };
  notify();
}

/** Forgets one channel's room membership - a rejoin the server rejected
 * (403, membership revoked while away). */
export function clearCueRoomState(channelId: string): void {
  if (!(channelId in state.rooms)) return;
  const rooms = { ...state.rooms };
  delete rooms[channelId];
  state = { ...state, rooms };
  notify();
}

/** Forgets every room - a deliberate disconnect (logout, credential change). */
export function clearAllCueRooms(): void {
  if (Object.keys(state.rooms).length === 0) return;
  state = { ...state, rooms: {} };
  notify();
}

export function recordCueHeartbeatAck(rttMs: number): void {
  state = {
    ...state,
    lastHeartbeatAt: Date.now(),
    lastHeartbeatRttMs: rttMs,
    counters: { ...state.counters, heartbeatsAcked: state.counters.heartbeatsAcked + 1 },
  };
  notify();
}

/** Zeroes the running totals (not the event log) - the "Reset" control on
 * the Diagnostics screen. */
export function resetCueCounters(): void {
  state = { ...state, counters: emptyCounters() };
  notify();
}

export function recordCueEvent(fields: Omit<CueDiagnosticEvent, 'id' | 'timestamp'>): void {
  const event: CueDiagnosticEvent = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: Date.now(),
    ...fields,
  };
  const events = state.events.length >= MAX_EVENTS ? [...state.events.slice(state.events.length - MAX_EVENTS + 1), event] : [...state.events, event];
  // An inbound relay for a channel is what makes its dot / info button
  // blink - stamp the arrival time so the UI can show a ~1s pulse.
  const lastActivityAt =
    event.kind === 'relay' && event.direction === 'in' && event.channelId
      ? { ...state.lastActivityAt, [event.channelId]: event.timestamp }
      : state.lastActivityAt;
  state = { ...state, events, lastActivityAt, counters: applyEventToCounters(state.counters, event) };
  notify();
}

// Fold one event into the running totals. Kept next to recordCueEvent (its
// only caller) rather than inlined so the branching stays readable.
function applyEventToCounters(counters: CueCounters, event: CueDiagnosticEvent): CueCounters {
  const next: CueCounters = { ...counters, byChannel: counters.byChannel };
  const bumpChannel = (channelId: string, key: keyof CueChannelTraffic) => {
    const existing = next.byChannel[channelId] ?? { in: 0, out: 0 };
    next.byChannel = { ...next.byChannel, [channelId]: { ...existing, [key]: existing[key] + 1 } };
  };

  if (event.kind === 'command' && event.direction === 'out') {
    if (event.command === CueCommand.sessionHeartbeat) next.heartbeatsSent += 1;
    else if (event.command === CueCommand.channelJoin) next.joinsSent += 1;
    else if (event.command === CueCommand.round) {
      next.roundsSent += 1;
      if (event.channelId) bumpChannel(event.channelId, 'out');
    } else if (event.command === CueCommand.message || event.command === CueCommand.templateSignal) {
      next.messagesSent += 1;
      if (event.channelId) bumpChannel(event.channelId, 'out');
    }
  } else if (event.kind === 'relay' && event.direction === 'in') {
    next.messagesReceived += 1;
    if (event.channelId) bumpChannel(event.channelId, 'in');
  } else if (event.kind === 'timeout') {
    next.timeouts += 1;
  } else if (event.kind === 'error') {
    next.errors += 1;
  }

  return next;
}

/** For useSyncExternalStore's subscribe. */
export function subscribeCueDiagnostics(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** For useSyncExternalStore's getSnapshot - same reference until something changes. */
export function getCueDiagnosticsSnapshot(): CueDiagnosticsSnapshot {
  return state;
}

/** Shared color/label mapping for connectionState - used by both
 * AppLayout's always-on badge and DiagnosticsScreen's fuller status row,
 * kept in one place so the two can't silently drift apart. */
export function cueConnectionStatusMeta(state: CueConnectionState): { color: string; label: string } {
  switch (state) {
    case 'connected':
      return { color: 'bg-emerald-500', label: 'Connected' };
    case 'connecting':
      return { color: 'bg-amber-500', label: 'Connecting…' };
    case 'disconnected':
      return { color: 'bg-red-500', label: 'Disconnected' };
  }
}

/** What a wire command code actually is, in plain English - shared by
 * describeCueEvent below for every kind that names a command. */
function commandLabel(command: string | undefined): string {
  switch (command) {
    case CueCommand.sessionHeartbeat:
      return 'heartbeat';
    case CueCommand.channelJoin:
      return 'join';
    case CueCommand.message:
      return 'message';
    case CueCommand.templateSignal:
      return 'template signal';
    default:
      return command ?? 'message';
  }
}

/** Turns one raw wire event into a plain-English sentence for the
 * Diagnostics screen's event log - e.g. "Sent template signal #2 to
 * @newchannel" instead of "mt @newchannel.1 2". Kept as a pure function
 * (not baked into how the event is recorded) so the raw fields stay
 * available for anything that wants the technical shorthand too. */
export function describeCueEvent(event: CueDiagnosticEvent): string {
  const channel = event.channelId ? ` to ${event.channelId}` : '';
  const from = event.channelId ? ` in ${event.channelId}` : '';
  switch (event.kind) {
    case 'command':
      if (event.command === CueCommand.sessionHeartbeat) return 'Sent heartbeat';
      if (event.command === CueCommand.channelJoin) return `Joined ${event.channelId ?? 'channel'}`;
      if (event.command === CueCommand.templateSignal) {
        const number = event.value?.split(MESSAGE_VALUE_SEP)[0] ?? event.value;
        return `Sent template signal #${number}${channel}`;
      }
      if (event.command === CueCommand.message) return `Sent message${channel}${event.value ? `: “${truncate(event.value, 40)}”` : ''}`;
      return `Sent ${commandLabel(event.command)}${channel}`;
    case 'reply': {
      const rtt = event.rttMs !== undefined ? ` (${event.rttMs}ms)` : '';
      const label = event.command === CueCommand.sessionHeartbeat ? 'Heartbeat acknowledged' : `${capitalize(commandLabel(event.command))} confirmed by server`;
      const unexpected = event.status !== undefined && event.status !== 202 ? ` — unexpected status ${event.status}` : '';
      return `${label}${rtt}${unexpected}`;
    }
    case 'timeout':
      return `Timed out waiting for a reply to the ${commandLabel(event.command)}${from}`;
    case 'relay':
      return `Received a ${commandLabel(event.command)} from another client${from}`;
    case 'error':
      return event.note ?? 'Something went wrong with an incoming message';
  }
}

/** A short bucket name for one event - the key the Diagnostics screen's
 * "Group by type" view collapses the In/Out log by. Coarser than the raw
 * command/kind pair: a heartbeat send and its reply both read "Heartbeat",
 * a timeout is its own bucket regardless of which command timed out. */
export function eventTypeLabel(event: CueDiagnosticEvent): string {
  if (event.kind === 'timeout') return 'Timeouts';
  if (event.kind === 'error') return 'Errors';
  if (event.command === CueCommand.sessionHeartbeat) return 'Heartbeats';
  if (event.command === CueCommand.channelJoin) return 'Joins';
  if (event.command === CueCommand.templateSignal) return 'Template signals';
  if (event.command === CueCommand.message) return 'Messages';
  if (event.command === CueCommand.round) return 'Round controls';
  if (event.kind === 'relay') return 'Relays';
  if (event.kind === 'reply') return 'Replies';
  return capitalize(commandLabel(event.command)) + 's';
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}
